/**
 * DisputesTab - Admin disputes management tab
 * Full feature parity with inline implementation
 */
import { motion } from 'framer-motion';
import { ShieldAlert, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../context/LanguageContext';

export function DisputesTab({ 
  disputes,
  disputeStats,
  onRefresh,
  formatDate
}) {
  const { language } = useLanguage();

  return (
    <motion.div
      key="disputes-tab"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="space-y-4"
    >
      {/* Dispute Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-red-600">{language === 'es' ? 'Abiertas' : 'Open'}</span>
          <p className="text-2xl font-bold text-red-600">{disputeStats?.open || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-amber-600">{language === 'es' ? 'En Revisión' : 'Under Review'}</span>
          <p className="text-2xl font-bold text-amber-600">{disputeStats?.under_review || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-emerald-600">{language === 'es' ? 'Ganadas' : 'Won'}</span>
          <p className="text-2xl font-bold text-emerald-600">{disputeStats?.won || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-slate-500">{language === 'es' ? 'Perdidas' : 'Lost'}</span>
          <p className="text-2xl font-bold text-slate-600">{disputeStats?.lost || 0}</p>
        </div>
      </div>

      {/* Disputes Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="disputes-table">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-600" />
            {language === 'es' ? 'Disputas y Contracargos' : 'Disputes & Chargebacks'}
          </h3>
          {onRefresh && (
            <Button size="sm" variant="outline" onClick={onRefresh}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          )}
        </div>
        {disputes.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <ShieldAlert className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>{language === 'es' ? 'No hay disputas todavía' : 'No disputes yet'}</p>
            <p className="text-sm mt-2">{language === 'es' ? 'Las disputas aparecen desde los webhooks de Stripe' : 'Disputes appear from Stripe webhooks'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{language === 'es' ? 'ID Disputa' : 'Dispute ID'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{language === 'es' ? 'Monto' : 'Amount'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{language === 'es' ? 'Razón' : 'Reason'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{language === 'es' ? 'Estado' : 'Status'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{language === 'es' ? 'Evidencia' : 'Evidence'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{language === 'es' ? 'Fecha' : 'Date'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {disputes.map((dispute) => (
                  <tr key={dispute.id} className="hover:bg-slate-50" data-testid={`dispute-${dispute.dispute_id}`}>
                    <td className="px-6 py-4 font-mono text-xs text-slate-900">{dispute.dispute_id}</td>
                    <td className="px-6 py-4 font-semibold text-red-600">${dispute.amount?.toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">{dispute.reason}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium capitalize ${
                        dispute.status === 'won' ? 'bg-emerald-50 text-emerald-700' :
                        dispute.status === 'lost' ? 'bg-red-50 text-red-700' :
                        dispute.status === 'open' ? 'bg-amber-50 text-amber-700' :
                        'bg-slate-50 text-slate-600'
                      }`}>
                        {dispute.status === 'open' && <AlertTriangle className="w-3 h-3" />}
                        {dispute.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500 capitalize">{dispute.evidence_status?.replace(/_/g, ' ')}</td>
                    <td className="px-6 py-4 text-sm text-slate-500">{formatDate(dispute.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default DisputesTab;
